
import React from 'react';
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle, Clock } from "lucide-react";
import { SettingsSectionProps } from '../types';
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

const YearDurationSettings = ({ localSettings, setLocalSettings }: SettingsSectionProps) => {
  // Format seconds as minutes and seconds
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    if (mins > 0) {
      return `${mins} min${mins !== 1 ? 's' : ''} ${secs > 0 ? `${secs} sec${secs !== 1 ? 's' : ''}` : ''}`;
    }
    
    return `${secs} sec${secs !== 1 ? 's' : ''}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-5 border border-white/10 bg-gradient-to-br from-muted/50 to-background shadow-md hover:shadow-lg transition-all">
        <div className="space-y-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Label htmlFor="yearDuration" className="text-lg font-medium text-foreground font-playfair">Year Duration</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div whileHover={{ rotate: 15 }} transition={{ duration: 0.2 }}>
                      <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-card border-white/10 backdrop-blur-sm">
                    <p className="w-[200px] text-sm">Set how long each simulation year lasts in real time</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <motion.div 
              className="font-semibold text-md relative"
              key={localSettings.yearDuration}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                {formatDuration(localSettings.yearDuration)}
              </span>
              <div className="absolute inset-0 -z-10 blur-sm opacity-30 bg-primary rounded-full"></div>
            </motion.div>
          </div>
          
          <Slider 
            id="yearDuration"
            min={60}
            max={300}
            step={30}
            value={[localSettings.yearDuration]}
            className="h-2"
            onValueChange={(value) => 
              setLocalSettings({
                ...localSettings,
                yearDuration: value[0]
              })
            }
          />
          
          <motion.p 
            className="text-sm text-muted-foreground/90 italic"
            key={localSettings.yearDuration}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {localSettings.yearDuration <= 120 
              ? "Faster paced simulation with less time per year" 
              : localSettings.yearDuration >= 240 
                ? "Slower paced simulation with more time to make decisions"
                : "Balanced simulation pace with moderate decision time"}
          </motion.p>
        </div>
      </Card>
    </motion.div>
  );
};

export default YearDurationSettings;
